import { PagedResultDto, RestService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import {
  GetIntentsInput,
  IntentWithNavigationPropertiesDto,
  VirtualAgentStatusDto,
} from 'src/app/virtual-agents/proxy';
import {
  DeletionStatusDto,
  ContextCreateDto,
  ContextUpdateDto,
  ContextDto,
  ContextEditDto,
  FlowDto,
  Flows,
  FlowUpdateDto,
  MandatoryFieldsResponseDto,
  NodeModel,
  UiPaletteOutputDto,
  FlowNameUpdateDto,
  FlowCreateDto,
  ProjectVersionSubFlowDto,
  FlowType,
} from './designer.model';
import { AiAgentPreparationStatusDto } from 'src/app/ai-agents/proxy/ai-agents.model';
import { KnowledgeBaseStatusDto } from 'src/app/knowledge-bases/proxy/knowledge-base.model';

const FLOWS_PATH = '/api/app/flows';
const PROJECT_VERSION = '/api/app/projectVersions';
const CONTEXT_PATH = '/api/app/contexts';

@Injectable({
  providedIn: 'root',
})
export class DesignerService {
  apiName = 'Default';

  getPaletteModels = (id: string, flowType: FlowType) =>
    this.restService.request<void, UiPaletteOutputDto[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/node-palette/${id}?flowType=${flowType}`,
      },
      { apiName: this.apiName },
    );

  getSubFlowDetailsByProjectVersionId = (
    projectVersionId: string,
    flowType: FlowType,
    agentName: string | null,
  ) =>
    this.restService.request<void, ProjectVersionSubFlowDto[]>(
      {
        method: 'GET',
        url: `${PROJECT_VERSION}/${projectVersionId}/sub-flow-details?flowType=${flowType}&agentName=${agentName}`,
      },
      { apiName: this.apiName },
    );

  getCustomDataKeys = (projectVersionId: string) =>
    this.restService.request<void, string[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/custom-data-keys/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  getFlowById = (id: string) =>
    this.restService.request<void, FlowDto>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/${id}`,
      },
      { apiName: this.apiName },
    );

  updateFlowJson = (params: any) =>
    this.restService.request<any, any>(
      {
        method: 'PUT',
        url: `${FLOWS_PATH}/update-flow-json`,
        body: params,
      },
      { apiName: this.apiName },
    );

  updateSubFlow = (params: FlowUpdateDto) =>
    this.restService.request<FlowUpdateDto, Flows>(
      {
        method: 'PUT',
        url: `${FLOWS_PATH}/${params.id}`,
        body: params,
      },
      { apiName: this.apiName },
    );

  updateSubFlowName = (id: string, params: FlowNameUpdateDto) =>
    this.restService.request<FlowNameUpdateDto, Flows>(
      {
        method: 'PATCH',
        url: `${FLOWS_PATH}/${id}/name`,
        body: params,
      },
      { apiName: this.apiName },
    );

  createSubFlow = (params: FlowCreateDto) =>
    this.restService.request<FlowCreateDto, Flows>(
      {
        method: 'POST',
        url: FLOWS_PATH,
        body: params,
      },
      { apiName: this.apiName },
    );

  deleteSubFlow = (id: string) =>
    this.restService.request<void, void>(
      {
        method: 'DELETE',
        url: `${FLOWS_PATH}/${id}`,
      },
      { apiName: this.apiName },
    );

  getAvailableVirtualAgents = (projectVersionId: string) =>
    this.restService.request<void, VirtualAgentStatusDto[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/available-virtual-agents/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  getAvailableAiAgents = (projectVersionId: string) =>
    this.restService.request<void, AiAgentPreparationStatusDto[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/available-ai-agents/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  getAvailableKnowledgeBases = (projectVersionId: string) =>
    this.restService.request<void, KnowledgeBaseStatusDto[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/available-knowledge-bases/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  getVoiceBiometryIntegrationDefaultConfidenceThreshold = (projectVersionId: string) =>
    this.restService.request<void, string>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/${projectVersionId}/voice-biometrics/default-confidence-threshold`,
      },
      { apiName: this.apiName },
    );

  writeAudioFileToS3Async = (file: File, key: string) => {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('key', key);

    return this.restService.request<FormData, void>(
      {
        method: 'POST',
        url: `${FLOWS_PATH}/audio-file`,
        body: formData,
      },
      { apiName: this.apiName },
    );
  };

  writeFileToS3Async = (file: File, key: string) => {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('key', key);

    return this.restService.request<FormData, void>(
      {
        method: 'POST',
        url: `${FLOWS_PATH}/file`,
        body: formData,
      },
      { apiName: this.apiName },
    );
  };

  writeSsmlAudioFileToS3Async = (file: File, key: string) => {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('key', key);

    return this.restService.request<FormData, void>(
      {
        method: 'POST',
        url: `${FLOWS_PATH}/ssml-audio-file`,
        body: formData,
      },
      { apiName: this.apiName },
    );
  };

  exportFlow = (id: string) =>
    this.restService.request<void, any>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/export/${id}`,
      },
      { apiName: this.apiName },
    );

  getIntentsOfAvailableVirtualAgents = (input: GetIntentsInput, vId: string) =>
    this.restService.request<void, PagedResultDto<IntentWithNavigationPropertiesDto>>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/available-virtual-agents/${vId}/intents`,
        params: {
          filterText: input.filterText,
          name: input.name,
          sorting: input.sorting,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName },
    );

  checkMandatoryFields = (flowId: string, flowType: FlowType) =>
    this.restService.request<void, MandatoryFieldsResponseDto>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/check-mandatory-fields/${flowId}?flowType=${flowType}`,
      },
      { apiName: this.apiName },
    );

  checkNodeValidations = (nodeData: NodeModel, projectVersionId: string) =>
    this.restService.request<any, boolean>(
      {
        method: 'POST',
        url: `${FLOWS_PATH}/check-node-validations?projectVersionId=${projectVersionId}`,
        body: nodeData,
      },
      { apiName: this.apiName },
    );

  getLanguageNames = (projectVersionId: string) =>
    this.restService.request<void, string[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/language-names/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  getSkills = (projectVersionId: string) =>
    this.restService.request<void, string[]>(
      {
        method: 'GET',
        url: `${FLOWS_PATH}/skills/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  // Context

  getProjectContexts = (
    projectVersionId: string,
    intentId: string = null,
    aiAgentToolId: string = null,
  ) =>
    this.restService.request<void, ContextDto[]>(
      {
        method: 'GET',
        url: intentId
          ? `${CONTEXT_PATH}/${projectVersionId}?intentId=${intentId}`
          : aiAgentToolId
            ? `${CONTEXT_PATH}/${projectVersionId}?aiAgentToolId=${aiAgentToolId}`
            : `${CONTEXT_PATH}/${projectVersionId}`,
      },
      { apiName: this.apiName },
    );

  checkIfContextIsDeletable = (id) =>
    this.restService.request<void, DeletionStatusDto>(
      {
        method: 'POST',
        url: `${CONTEXT_PATH}/check-if-deletable/${id}`,
      },
      { apiName: this.apiName },
    );

  deleteContext = (id) =>
    this.restService.request<void, boolean>(
      {
        method: 'DELETE',
        url: `${CONTEXT_PATH}/${id}`,
      },
      { apiName: this.apiName },
    );

  updateContext = (id: string, params: ContextUpdateDto) =>
    this.restService.request<ContextUpdateDto, ContextEditDto>(
      {
        method: 'PUT',
        url: `${CONTEXT_PATH}/${id}`,
        body: params,
      },
      { apiName: this.apiName },
    );

  createContext = (id: string, params: ContextCreateDto) =>
    this.restService.request<ContextCreateDto, ContextEditDto>(
      {
        method: 'POST',
        url: `${CONTEXT_PATH}/${id}`,
        body: params,
      },
      { apiName: this.apiName },
    );

  constructor(private restService: RestService) {}
}
